import { useState } from 'react';

import { useMountEffect } from '@react-hookz/web';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Card } from 'src/app/components/Elements/Card';
import { StylizedCardItemLinkWithIcon } from 'src/app/components/Elements/Card/StylizedCardItemLinkWithIcon';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import Spinner from 'src/app/components/Elements/Spinner';
import * as paths from 'src/app/router/paths';
import type { DeliveryMethodOption, UpdateCartPaymentMethodRequest } from 'src/app/types';
import { CAPSULE_ICON_STRINGS, DeliveryMethodType } from 'src/consts';

import type { PropsFromRedux } from './container';

const handleDeliveryMethodSection = (
  deliveryMethod: DeliveryMethodOption,
  cartId: string,
  cartUUID: string,
  defaultCardId: string,
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => void,
  dispatchUpdateCartAndPushNextCheckoutState: (
    cartUUID: string,
    paymentMethodBody: UpdateCartPaymentMethodRequest
  ) => void,
  setIsSubmitting: (isSubmitting: boolean) => void
) => {
  const { key, name, description } = deliveryMethod;

  const handleOnSubmit = e => {
    e.preventDefault();
    dispatchUpdateCartAndPushNextCheckoutState(cartUUID, { cartUUID, paymentMethodId: defaultCardId });
    setIsSubmitting(true);
  };

  const handleOnClick = e => {
    if (key === DeliveryMethodType.mail) handleOnSubmit(e);
    else if (key === DeliveryMethodType.courier)
      dispatchSegmentTrack(UNIFIED.selectDeliveryMethod, {
        delivery_method: `${key}`,
      });
  };

  if (key === DeliveryMethodType.courier) {
    return (
      <StylizedCardItemLinkWithIcon
        sectionTitle={name}
        capsuleIcon={CAPSULE_ICON_STRINGS.clock}
        handleOnClick={handleOnClick}
        to={paths.checkoutSelectWindow.toPath({ cartId })}
        ariaLabel={'Choose Capsule Same Day Delivery'}
        textList={[description]}
        isDisabled={false}
        key="courier_option"
        showArrow={false}
      />
    );
  } else if (key === DeliveryMethodType.mail) {
    return (
      <StylizedCardItemLinkWithIcon
        sectionTitle={name}
        capsuleIcon={CAPSULE_ICON_STRINGS.mail}
        to={'#'}
        handleOnClick={handleOnClick}
        ariaLabel={'Choose Express Scripts Delivery'}
        textList={[description]}
        isDisabled={false}
        key={'mail_option'}
        showArrow={false}
      />
    );
  } else {
    return null;
  }
};

interface DeliveryMethodsRendererProps extends PropsFromRedux {}

const DeliveryMethodsRenderer = ({
  bffCartDeliveryMethods,
  cartId,
  cartUUID,
  defaultCardId,
  deliveryMethodNames,
  dispatchSegmentTrack,
  isFetchedBFFCartDeliveryMethods,
  isFetchingBFFCartDeliveryMethods,
  dispatchLoadCartDeliveryMethods,
  dispatchUpdateCartAndPushNextCheckoutState,
}: DeliveryMethodsRendererProps) => {
  const [hasSegmentEventFired, setHasSegmentEventFired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useMountEffect(() => {
    if (isFetchedBFFCartDeliveryMethods && !hasSegmentEventFired && !isFetchingBFFCartDeliveryMethods) {
      dispatchSegmentTrack(UNIFIED.viewDeliveryMethod, { available_delivery_methods: deliveryMethodNames });
      setHasSegmentEventFired(true);
    }

    if (!isFetchedBFFCartDeliveryMethods && !isFetchingBFFCartDeliveryMethods) {
      dispatchLoadCartDeliveryMethods();
    }
  });

  if (!isFetchedBFFCartDeliveryMethods || !isFetchingBFFCartDeliveryMethods || isSubmitting) {
    <StandardPage title={'Choose a delivery time'}>
      <Spinner centered />
    </StandardPage>;
  }

  return (
    <StandardPage title={'Choose a delivery time'}>
      <Card>
        {bffCartDeliveryMethods?.map(bffCartDeliveryMethod =>
          handleDeliveryMethodSection(
            bffCartDeliveryMethod,
            cartId,
            cartUUID,
            defaultCardId,
            dispatchSegmentTrack,
            dispatchUpdateCartAndPushNextCheckoutState,
            setIsSubmitting
          )
        )}
      </Card>
    </StandardPage>
  );
};

export default DeliveryMethodsRenderer;
